import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach Authorization header if token in localStorage (Bearer fallback)
const PLATFORM_ALLOWED = ["/platform/", "/auth/"];

api.interceptors.request.use((config) => {
  if (typeof window !== "undefined" && window.__wcPlatformLocked
      && !PLATFORM_ALLOWED.some((p) => (config.url || "").startsWith(p))) {
    return Promise.reject(new axios.Cancel("Shop API skipped for platform console"));
  }
  const token = localStorage.getItem("wc_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const vault = sessionStorage.getItem("wc_platform_vault_token");
  if (vault) {
    config.headers = config.headers || {};
    config.headers["X-Vault-Token"] = vault;
  }
  return config;
});

/**
 * Global 401 handler: whenever the backend rejects a request because the
 * session is missing/expired, wipe the stale token and bounce the user to
 * /login with a clear message. This turns the confusing "Not authenticated"
 * toast into a graceful re-login prompt.
 *
 * Auth endpoints themselves (login / change-password) are exempted so their
 * 401s still bubble as inline form errors ("current password is incorrect").
 */
const AUTH_PATHS = ["/auth/login", "/auth/change-password", "/auth/register", "/platform/vault/unlock"];
export const FLASH_KEY = "wc_login_flash";
export const AUTH_LOGOUT_EVENT = "wc:auth-logout";

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthCall = AUTH_PATHS.some((p) => url.includes(p));
    if (status === 423 && typeof window !== "undefined") {
      // Vault token expired/missing — drop it so the lock screen reappears.
      sessionStorage.removeItem("wc_platform_vault_token");
      window.dispatchEvent(new Event("wc:vault-locked"));
    }
    if (status === 401 && !isAuthCall && typeof window !== "undefined") {
      const hadToken = !!localStorage.getItem("wc_token");
      localStorage.removeItem("wc_token");
      if (err.response && err.response.data) {
        err.response.data.detail = "Your session expired — please sign in again.";
      }
      // Stash a one-shot flash + dispatch a soft-logout event.
      // AuthContext listens and flips user=false; ProtectedRoute then does a
      // React Router <Navigate> to /login (no hard reload, no double-mount).
      // This eliminates the previous race where a hard window.location.assign
      // was destroying the transient banner state before it could render.
      if (!window.location.pathname.startsWith("/login")) {
        try {
          sessionStorage.setItem(
            FLASH_KEY,
            hadToken
              ? "Your session expired — please sign in again."
              : "Please sign in to continue."
          );
        } catch (_) { /* private mode / storage disabled — best-effort */ }
        try {
          window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
        } catch (_) { /* ignore */ }
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
